// "Pricing" card of a charter request: indicative price, pre-auth and the removable ad-hoc labels.
import { Card, Descriptions, Space, Typography } from 'antd';

import type { CharterRequestDetail } from '../../api/types';
import { PreAuthStateTag } from '../../components/CharterInvoicesCard';
import { AdhocTag } from '../../components/AdhocTag';
import { ReleaseFailedTag } from '../../components/CancellationMoneyTags';
import { gbp } from '../../data/format';

const { Text } = Typography;

export function PricingCard({
  r,
  dismissQuoteRefreshMut,
  dismissPaymentRequiredMut,
}: {
  r: CharterRequestDetail;
  dismissQuoteRefreshMut: { isPending: boolean; mutate: () => void };
  dismissPaymentRequiredMut: { isPending: boolean; mutate: () => void };
}) {
  return (
    <Card title="Pricing">
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
        <Descriptions.Item label="Indicative price">{r.indicativePriceGbp != null ? `${gbp(r.indicativePriceGbp)}` : '—'}</Descriptions.Item>
        <Descriptions.Item label="Pre-auth">
          {/* SHAR-1109 — the amount and what became of it: Released on a
              cancelled request, Expired when the hold lapsed, Captured once a
              quote was paid (as the ShareJet invoices say it). */}
          {r.preAuthAmountGbp != null ? (
            <Space size={6} wrap>
              {gbp(r.preAuthAmountGbp)}
              <PreAuthStateTag state={r.preAuthState} at={r.preAuthStateAt} reason={r.preAuthReleaseReason} />
              <ReleaseFailedTag at={r.preAuthReleaseFailedAt} reason={r.preAuthReleaseFailure} />
            </Space>
          ) : r.preAuthExpired ? (
            <Text type="danger">Expired</Text>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        {/* SHAR-1105 — "Payment required" (SHAR-1092) as an ad-hoc field of its
            own, removable with a confirm; the admin panel only. */}
        {r.adhocPaymentRequired || r.adhocQuoteRefresh ? (
          <Descriptions.Item label="Ad-hoc status" span={2}>
            <Space size={8} wrap>
              {/* SHAR-1116 — "Quote refresh", removable with a confirm. */}
              {r.adhocQuoteRefresh ? (
                <AdhocTag
                  color="gold"
                  label="Quote refresh"
                  title="Remove label"
                  description="Are you sure you want to remove the Quote refresh status?"
                  loading={dismissQuoteRefreshMut.isPending}
                  onRemove={() => dismissQuoteRefreshMut.mutate()}
                />
              ) : null}
              {r.adhocPaymentRequired ? (
                <AdhocTag
                  color="red"
                  label="Payment required"
                  title="Remove label"
                  description="Remove the “Payment required” label from this charter request? It is removed in the admin panel only; the customer still sees it."
                  loading={dismissPaymentRequiredMut.isPending}
                  onRemove={() => dismissPaymentRequiredMut.mutate()}
                />
              ) : null}
            </Space>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </Card>
  );
}
